import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Globe, 
  Palette, 
  Terminal, 
  Lightbulb,
  Megaphone,
  Search,
  BarChart3,
  Shield,
  Bot,
  Calendar,
  Database,
  Store,
  WholeWord as WordPress,
  Settings,
  CreditCard,
  Zap,
  Server,
  Mail,
  MessageSquare,
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  X,
  Menu,
  ChevronDown,
  LifeBuoy
} from 'lucide-react';
import LanguagePicker from './LanguagePicker';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { 
      icon: Globe, 
      title: t('nav.services.webDevelopment.title'),
      description: t('nav.services.webDevelopment.description'),
      path: '/thjonusta/vefsidugerd'
    },
    { 
      icon: Palette, 
      title: t('nav.services.webDesign.title'),
      description: t('nav.services.webDesign.description'),
      path: '/thjonusta/vefhonnun'
    },
    { 
      icon: Terminal, 
      title: t('nav.services.webProgramming.title'),
      description: t('nav.services.webProgramming.description'),
      path: '/thjonusta/vefthroun'
    },
    { 
      icon: Lightbulb, 
      title: t('nav.services.consulting.title'),
      description: t('nav.services.consulting.description'),
      path: '/thjonusta/radgjof'
    },
    { 
      icon: Megaphone, 
      title: t('nav.services.marketing.title'),
      description: t('nav.services.marketing.description'),
      path: '/thjonusta/markadssetning'
    },
    { 
      icon: Search, 
      title: t('nav.services.seo.title'),
      description: t('nav.services.seo.description'),
      path: '/thjonusta/leitarvelabestun'
    },
    { 
      icon: BarChart3, 
      title: t('nav.services.analytics.title'),
      description: t('nav.services.analytics.description'),
      path: '/thjonusta/greiningar'
    },
    { 
      icon: Shield, 
      title: t('nav.services.maintenance.title'),
      description: t('nav.services.maintenance.description'),
      path: '/thjonusta/vidhald'
    }
  ];

  const solutions = [
    { 
      icon: Bot, 
      title: t('nav.solutions.ai.title'),
      description: t('nav.solutions.ai.description'),
      path: '/lausnir/gervigreind'
    },
    { 
      icon: Calendar, 
      title: t('nav.solutions.booking.title'),
      description: t('nav.solutions.booking.description'),
      path: '/lausnir/bokunarkerfi'
    },
    { 
      icon: Database, 
      title: t('nav.solutions.subscription.title'),
      description: t('nav.solutions.subscription.description'),
      path: '/lausnir/askriftarkerfi'
    },
    { 
      icon: Store, 
      title: t('nav.solutions.ecommerce.title'),
      description: t('nav.solutions.ecommerce.description'),
      path: '/lausnir/netverslun'
    },
    { 
      icon: CreditCard, 
      title: t('nav.solutions.pos.title'),
      description: t('nav.solutions.pos.description'),
      path: '/lausnir/kassakerfi'
    },
    { 
      icon: WordPress, 
      title: t('nav.solutions.wordpress.title'),
      description: t('nav.solutions.wordpress.description'),
      path: '/lausnir/wordpress'
    },
    { 
      icon: Zap, 
      title: t('nav.solutions.wordpressExpress.title'),
      description: t('nav.solutions.wordpressExpress.description'),
      path: '/lausnir/wordpress-express'
    },
    { 
      icon: Server, 
      title: t('nav.solutions.hosting.title'),
      description: t('nav.solutions.hosting.description'),
      path: '/lausnir/vefhysing'
    },
    { 
      icon: Mail, 
      title: t('nav.solutions.email.title'),
      description: t('nav.solutions.email.description'),
      path: '/lausnir/tolvupostur'
    },
    { 
      icon: Shield, 
      title: t('nav.solutions.management.title'),
      description: t('nav.solutions.management.description'),
      path: '/lausnir/vefumsjon'
    },
    {
      icon: MessageSquare,
      title: t('nav.solutions.chat.title'),
      description: t('nav.solutions.chat.description'),
      path: '/lausnir/netspjall'
    }
  ];

  return (
    <motion.nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-sm'
          : 'bg-transparent'
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://res.cloudinary.com/djimcqajg/image/upload/v1741340483/vefsida-logo-v2_y5imtb.svg" 
                alt="Vefsíða" 
                className="h-8 w-auto"
              />
            </Link>

            <LanguagePicker />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              <Link 
                to="/" 
                className={`px-4 py-2 text-[15px] font-medium rounded-full transition-colors ${
                  location.pathname === '/' 
                    ? 'text-[#EA295A] bg-[#EA295A]/5'
                    : 'text-gray-600 hover:text-[#EA295A] hover:bg-gray-50'
                }`}
              >
                {t('nav.home')}
              </Link>

              <Link 
                to="/fyrirtaekid" 
                className={`px-4 py-2 text-[15px] font-medium rounded-full transition-colors ${
                  location.pathname === '/fyrirtaekid' 
                    ? 'text-[#EA295A] bg-[#EA295A]/5'
                    : 'text-gray-600 hover:text-[#EA295A] hover:bg-gray-50'
                }`}
              >
                {t('nav.company')}
              </Link>

              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setActiveSection('services')}
                onMouseLeave={() => setActiveSection(null)}
              >
                <button 
                  className={`px-4 py-2 text-[15px] font-medium rounded-full transition-colors inline-flex items-center ${
                    location.pathname.startsWith('/thjonusta') || activeSection === 'services'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-600 hover:text-[#EA295A] hover:bg-gray-50'
                  }`}
                >
                  {t('nav.services.title')}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>

                <AnimatePresence>
                  {activeSection === 'services' && (
                    <motion.div 
                      className="absolute left-0 mt-2 w-[500px] bg-white rounded-xl shadow-lg ring-1 ring-black/5 overflow-hidden"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="grid grid-cols-2 gap-2 p-4">
                        {services.map((service, index) => (
                          <Link 
                            key={index}
                            to={service.path}
                            className={`flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors ${
                              location.pathname === service.path
                                ? 'bg-[#EA295A]/5'
                                : ''
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <service.icon className="h-5 w-5 text-[#EA295A]" />
                            </div>
                            <div className="ml-3">
                              <p className="text-base font-medium text-gray-900">{service.title}</p>
                              <p className="mt-1 text-sm text-gray-600">{service.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Solutions Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setActiveSection('solutions')}
                onMouseLeave={() => setActiveSection(null)}
              >
                <button 
                  className={`px-4 py-2 text-[15px] font-medium rounded-full transition-colors inline-flex items-center ${
                    location.pathname.startsWith('/lausnir') || activeSection === 'solutions'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-600 hover:text-[#EA295A] hover:bg-gray-50'
                  }`}
                >
                  {t('nav.solutions.title')}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>

                <AnimatePresence>
                  {activeSection === 'solutions' && (
                    <motion.div 
                      className="absolute left-0 mt-2 w-[500px] bg-white rounded-xl shadow-lg ring-1 ring-black/5 overflow-hidden"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="grid grid-cols-2 gap-2 p-4">
                        {solutions.map((solution, index) => (
                          <Link 
                            key={index}
                            to={solution.path}
                            className={`flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors ${
                              location.pathname === solution.path
                                ? 'bg-[#EA295A]/5'
                                : ''
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <solution.icon className="h-5 w-5 text-[#EA295A]" />
                            </div>
                            <div className="ml-3">
                              <p className="text-base font-medium text-gray-900">{solution.title}</p>
                              <p className="mt-1 text-sm text-gray-600">{solution.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link 
                to="/verkefni" 
                className={`px-4 py-2 text-[15px] font-medium rounded-full transition-colors ${
                  location.pathname === '/verkefni' 
                    ? 'text-[#EA295A] bg-[#EA295A]/5'
                    : 'text-gray-600 hover:text-[#EA295A] hover:bg-gray-50'
                }`}
              >
                {t('nav.projects')}
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link 
                to="/thjonustubord" 
                className="bg-white text-[#EA295A] border border-[#EA295A] px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#EA295A]/5 transition-all flex items-center"
              >
                <LifeBuoy className="w-4 h-4 mr-2" />
                {t('nav.serviceDesk')}
              </Link>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link 
                to="/fa-tilbod" 
                className="bg-[#EA295A] text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center uppercase tracking-wide"
              >
                {t('nav.GetQuote')}
                <ArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-[#EA295A] transition-colors"
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="lg:hidden fixed inset-0 top-[80px] bg-white z-40"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-full overflow-y-auto pb-20">
              <div className="p-4 space-y-2">
                {/* Language Picker */}
                <div className="mb-4">
                  <LanguagePicker />
                </div>

                {/* Main navigation items */}
                <Link
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 text-lg font-medium rounded-xl transition-colors ${
                    location.pathname === '/'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t('nav.home')}
                </Link>
                <Link
                  to="/fyrirtaekid"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 text-lg font-medium rounded-xl transition-colors ${
                    location.pathname === '/fyrirtaekid'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t('nav.company')}
                </Link>

                {/* Services Section */}
                <div className="py-2">
                  <button
                    onClick={() => setActiveSection(activeSection === 'services' ? null : 'services')}
                    className="w-full px-4 py-3 text-left text-lg font-medium rounded-xl transition-colors flex items-center justify-between text-gray-900 hover:bg-gray-50"
                  >
                    {t('nav.services.title')}
                    <motion.div
                      animate={{ rotate: activeSection === 'services' ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {activeSection === 'services' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4"
                      >
                        {services.map((service, index) => (
                          <Link
                            key={index}
                            to={service.path}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-4 py-3 text-gray-600 hover:text-[#EA295A] rounded-xl transition-colors"
                          >
                            <service.icon className="w-5 h-5 mr-3" />
                            {service.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Solutions Section */}
                <div className="py-2">
                  <button
                    onClick={() => setActiveSection(activeSection === 'solutions' ? null : 'solutions')}
                    className="w-full px-4 py-3 text-left text-lg font-medium rounded-xl transition-colors flex items-center justify-between text-gray-900 hover:bg-gray-50"
                  >
                    {t('nav.solutions.title')}
                    <motion.div
                      animate={{ rotate: activeSection === 'solutions' ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {activeSection === 'solutions' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4"
                      >
                        {solutions.map((solution, index) => (
                          <Link
                            key={index}
                            to={solution.path}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center px-4 py-3 text-gray-600 hover:text-[#EA295A] rounded-xl transition-colors"
                          >
                            <solution.icon className="w-5 h-5 mr-3" />
                            {solution.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link
                  to="/verkefni"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 text-lg font-medium rounded-xl transition-colors ${
                    location.pathname === '/verkefni'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {t('nav.projects')}
                </Link>

                <Link
                  to="/thjonustubord"
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 text-lg font-medium rounded-xl transition-colors ${
                    location.pathname === '/thjonustubord'
                      ? 'text-[#EA295A] bg-[#EA295A]/5'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <LifeBuoy className="w-5 h-5 inline-block mr-2" />
                  {t('nav.serviceDesk')}
                </Link>

                {/* CTA Button */}
                <div className="pt-4">
                  <Link
                    to="/fa-tilbod"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full bg-[#EA295A] text-white px-6 py-4 rounded-xl text-lg font-medium text-center hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20"
                  >
                    {t('nav.GetQuote')}
                    <ArrowRight className="inline-block w-5 h-5 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;